import {
	CAMERA_BASE_DISTANCE,
	CAMERA_DISTANCE_PER_SPEED,
	CAMERA_VIEW_FACTOR,
	type PlayerIndex,
} from "../../sim";

export interface Rect {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface ViewportLayout extends Rect {
	player: PlayerIndex;
}

/**
 * World units visible vertically at a given ship speed. legacy: gluPerspective(45°) with the
 * camera at z = 700 + 50·speed (OpenGLRenderer.cpp), so the view widens as the ship speeds up.
 */
export function visibleWorldHeight(speed: number, perSpeed = CAMERA_DISTANCE_PER_SPEED): number {
	return CAMERA_VIEW_FACTOR * (CAMERA_BASE_DISTANCE + perSpeed * speed);
}

/** Phaser camera zoom (screen px per world unit) for a viewport of the given pixel height. */
export function cameraZoom(viewportHeightPx: number, speed: number, perSpeed?: number): number {
	return viewportHeightPx / visibleWorldHeight(speed, perSpeed);
}

/**
 * Splits the canvas into one viewport per viewer, left to right. Two local players pass
 * [1, 0]: the LEFT half follows player 2 (red, WASD side of the keyboard) and the RIGHT half
 * player 1 (blue, arrows), as in the original. A 1 px divider sits left of the centre line.
 */
export function splitViewports(
	width: number,
	height: number,
	viewers: readonly PlayerIndex[],
): ViewportLayout[] {
	const [first = 0, second] = viewers;
	if (second === undefined) return [{ player: first, x: 0, y: 0, width, height }];
	const half = Math.floor(width / 2);
	if (viewers.length === 2) {
		return [
			{ player: first, x: 0, y: 0, width: half - DIVIDER_PX, height },
			{ player: second, x: half, y: 0, width: width - half, height },
		];
	}
	// 3–4 viewers (P11.3): a 2×2 grid in reading order; with three the last quarter stays empty.
	const top = Math.floor(height / 2);
	return viewers.slice(0, 4).map((player, i) => {
		const right = i % 2 === 1;
		const bottom = i >= 2;
		return {
			player,
			x: right ? half : 0,
			y: bottom ? top : 0,
			width: right ? width - half : half - DIVIDER_PX,
			height: bottom ? height - top : top - DIVIDER_PX,
		};
	});
}

/** Who gets a viewport: every human player (two humans share the screen red-left, blue-right). */
export function viewersFor(players: number, aiPlayers: readonly PlayerIndex[]): PlayerIndex[] {
	const humans = Array.from({ length: players }, (_, i) => i).filter((i) => !aiPlayers.includes(i));
	if (humans.length === 0) return [0]; // AI-only (attract mode): follow blue
	// Red (WASD) left of blue (arrows) as in the original; green and yellow below.
	return humans.length >= 2 ? [1, 0, ...humans.slice(2)].filter((i) => humans.includes(i)) : humans;
}

export const DIVIDER_PX = 1;

/** legacy HUD: minimap of 100 units centred at (-140, -240) (y-up) from the viewport centre at z=700. */
const MINIMAP_SIZE_UNITS = 100;
const MINIMAP_OFFSET_UNITS = { x: -140, y: 240 };

export interface MinimapLayout {
	cx: number;
	cy: number;
	size: number;
}

/** Minimap placement in screen pixels for a viewport, scaled like the original HUD. */
export function minimapLayout(vp: Rect): MinimapLayout {
	const pxPerUnit = vp.height / visibleWorldHeight(0);
	const size = MINIMAP_SIZE_UNITS * pxPerUnit;
	const margin = size / 2 + 4;
	const cx = vp.x + vp.width / 2 + MINIMAP_OFFSET_UNITS.x * pxPerUnit;
	const cy = vp.y + vp.height / 2 + MINIMAP_OFFSET_UNITS.y * pxPerUnit;
	// Keep it fully inside narrow viewports.
	return {
		cx: Math.min(Math.max(cx, vp.x + margin), vp.x + vp.width - margin),
		cy: Math.min(Math.max(cy, vp.y + margin), vp.y + vp.height - margin),
		size,
	};
}
